<script setup>
import Loading from '../../Loading.vue';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import CommsChoiceGroup from '../../communications/CommsChoiceGroup.vue';
import CommsEditorPage from '../../communications/CommsEditorPage.vue';
import SegmentMultiselect from '../../communications/SegmentMultiselect.vue';
import SequenceRunPanel from '../../communications/SequenceRunPanel.vue';
import XlButton from '../../xl/XlButton.vue';
import CommsInfoBanner from '../../communications/CommsInfoBanner.vue';
import { COMMS_HINTS } from './hints';
import XlCard from '../../xl/XlCard.vue';
import XlTag from '../../xl/XlTag.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import { TERM_TIPS } from '../../../helpers/termTips';
import { SEQUENCES_NEEDS_RENEWAL_DATES } from '../../../helpers/hubEmptyStates';

import SequenceStepRow from '../../communications/SequenceStepRow.vue';
import { useSelection } from '../../communications/useSelection';
import {
  activateSequence,
  createSequence,
  deactivateSequence,
  deleteSequence,
  listSequences,
  updateSequence,
} from '../../../api/sequences';
import { estimateRecipients, listSegments } from '../../../api/segments';
import { listTemplates } from '../../../api/templates';
import { channelChips } from '../../communications/channelHelpers';
import {
  addDays,
  chainDates,
  chainHeading,
  chainRunTitle,
  chainSpan,
  clampDays,
  fromChain,
  renewalOutOfOrder,
  renewalTiming,
  shortDay,
  toChain,
} from '../../communications/sequenceChain';
import { plural } from '../../../helpers/plural';
import { longDate } from '../../../helpers/usDate';
import { useCommunicationsStore } from '../../../stores/communications';
import XlField from '../../xl/XlField.vue';
import XlDateTimeField from '../../xl/XlDateTimeField.vue';


defineProps({ needsRenewalDates: Boolean });

const NAME_MAX = 60;

// Draft first: it is the default, and a two-button control reads left to right.
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
];

const ANCHOR_OPTIONS = [
  { value: 'renewal', label: 'Renewal reminders' },
  { value: 'scratch', label: 'From scratch' },
];

// What a renewal sequence looks like before anybody edits it: the reminders an
// agency sends anyway. Starting empty made every new sequence begin with the
// same clicks. Each names a standard by the key TemplateController seeds it
// under, which survives a rename.
const DEFAULT_RENEWAL_STEPS = [
  { days: 120, key: 'renewal_120' },
  { days: 90, key: 'renewal_90' },
  { days: 30, key: 'final_notice_30' },
];
// Email is the only channel: chat went with Hub Announcements (XN-4389). A step
// saved with chat before then is saved back as email.
const CHANNELS = ['email'];
const SAVE_FAILED = 'Could not save this sequence. Please try again.';

const store = useCommunicationsStore();
const { selectedIds, isSelected, toggle, clear, selectedCount } = useSelection();

const sequences = ref([]);
const segments = ref([]);
const templates = ref([]);
const loadingList = ref(true);
const listError = ref('');
const segmentsError = ref(false);
const templatesError = ref(false);

const formOpen = ref(false);
const editingId = ref(null);
const saving = ref(false);
const saveError = ref('');

const form = reactive({
  name: '',
  status: 'draft',
  segment_ids: [],
  type: 'renewal',
  anchor_date: '',
  stepsByType: { renewal: [], scratch: [] },
});

const isRenewal = computed(() => form.type === 'renewal');
const steps = computed(() => form.stepsByType[form.type]);

const segmentNameById = computed(() =>
  Object.fromEntries(segments.value.map((segment) => [segment.id, segment.name]))
);

const selectedSegmentNames = computed(() =>
  form.segment_ids.map((id) => segmentNameById.value[id]).filter(Boolean)
);

// Deduped across the chosen segments — somebody in two of them is one person,
// and the panel is a promise about how many messages go out.
const recipientCount = ref(null);
let recipientSeq = 0;

watch(() => [...form.segment_ids], async (ids) => {
  const seq = ++recipientSeq;

  if (!ids.length) {
    recipientCount.value = null;
    return;
  }

  try {
    const data = await estimateRecipients(ids);
    if (seq === recipientSeq) recipientCount.value = data?.recipient_count ?? null;
  } catch {
    if (seq === recipientSeq) recipientCount.value = null;
  }
});

const nameBlank = computed(() => form.name.trim().length === 0);

const toDateOnlyString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayDateString = () => toDateOnlyString(new Date());

// activate() rejects a scratch sequence whose anchor_date has already
// passed; caught here so a create-then-activate round trip can't create an
// orphaned draft the editor never warned about.
const isAnchorDateInPast = computed(
  () => form.type === 'scratch' && Boolean(form.anchor_date) && toDateOnlyString(form.anchor_date) < todayDateString()
);

const activationDateError = computed(() =>
  form.status === 'active' && isAnchorDateInPast.value
    ? 'This date must be in the future to activate this sequence.'
    : ''
);

const canSave = computed(
  () =>
    !nameBlank.value &&
    form.segment_ids.length > 0 &&
    steps.value.length > 0 &&
    (form.type !== 'scratch' || Boolean(form.anchor_date)) &&
    !activationDateError.value
);

const anchorDay = computed(() => toDateOnlyString(form.anchor_date));
const dates = computed(() => (isRenewal.value ? [] : chainDates(anchorDay.value, steps.value)));

const stepHeading = (step, index) => (isRenewal.value ? renewalTiming(step) : chainHeading(index, dates.value[index]));

const templateName = (id) => templates.value.find((template) => template.id === id)?.name ?? 'No template';

const renewalTip = (step) => TERM_TIPS[step.offset_days === 0 ? 'Renewal date' : 'Renewal'];

const runRows = computed(() => steps.value.map((step, index) => ({
  title: isRenewal.value ? renewalTiming(step) : chainRunTitle(step, index, dates.value[index]),
  tip: isRenewal.value ? renewalTip(step) : '',
  detail: `Email · ${templateName(step.template_id)}`,
})));

const runSpan = computed(() => {
  const days = dates.value;
  if (isRenewal.value || days.length < 2 || !days[0]) return '';
  return `${plural(chainSpan(steps.value), 'day')} from first to last — ${longDate(days[0])} to ${longDate(days[days.length - 1])}.`;
});

const outOfOrder = computed(() => isRenewal.value && renewalOutOfOrder(steps.value));

const TYPE_LABELS = { renewal: 'Renewal reminders', scratch: 'From scratch' };

const segmentNamesLabel = (sequence) => {
  const ids = sequence.segment_ids || [];
  if (!ids.length) return 'No segments';
  return ids.map((id) => segmentNameById.value[id] ?? 'Unknown segment').join(', ');
};

import { useAuthStore } from '../../../stores/auth';
import { canAssignBrokers } from '../../../helpers/roles';

const auth = useAuthStore();
/**
 * A sequence running for months in the agency's name is an agency commitment,
 * so a broker can read one but not build, edit, activate or pause it.
 */
const canRunSequences = computed(() => canAssignBrokers(auth.userData));

const metaLine = (sequence) => `Segment: ${segmentNamesLabel(sequence)} · ${TYPE_LABELS[sequence.type]}`;

const stepWhen = (sequence, step) => {
  if (sequence.type === 'scratch' && sequence.anchor_date) {
    return shortDay(addDays(sequence.anchor_date.slice(0, 10), dayOffset(step)));
  }
  if (step.offset_days === 0) return 'Day of';
  return `${plural(step.offset_days, 'day')} ${step.direction}`;
};

const stepChipLabel = (sequence, step) =>
  `${stepWhen(sequence, step)} · ${channelChips(step.channels).join(' + ').toLowerCase()}`;

const statusLabel = (sequence) => (sequence.status === 'active' ? 'Active' : 'Draft');

const dayOffset = (step) => (step.direction === 'before' ? -step.offset_days : step.offset_days);

/**
 * The wait between two steps, shown only for a sequence pinned to a date. A
 * renewal sequence reads off its own anchor ("120 days before"), so the gap
 * between chips is already on their faces and a chevron is all that is needed.
 */
const gapAfter = (sequence, index) => {
  const steps = sequence.steps || [];
  if (sequence.type === 'renewal' || index >= steps.length - 1) return '';

  const days = Math.abs(dayOffset(steps[index + 1]) - dayOffset(steps[index]));

  return days ? `+${days}d` : '';
};

const fetchSegments = async () => {
  segmentsError.value = false;
  try {
    segments.value = await listSegments();
  } catch {
    segments.value = [];
    segmentsError.value = true;
  }
};

const fetchTemplates = async () => {
  templatesError.value = false;
  try {
    templates.value = await listTemplates();
  } catch {
    templates.value = [];
    templatesError.value = true;
  }
};

// Both lists are owned by other tabs and can change while this one is mounted,
// so the editor re-reads them on open rather than trusting its mount-time copy.
const refreshPickerOptions = () => Promise.all([fetchSegments(), fetchTemplates()]);

const fetchList = async () => {
  loadingList.value = true;
  listError.value = '';
  // Settled rather than all: a failing sequences call must not abandon the
  // picker fetches beside it, which is what leaves the audience selection
  // empty with nothing on screen explaining why.
  const [sequenceResult] = await Promise.allSettled([listSequences(), refreshPickerOptions()]);

  if (sequenceResult.status === 'fulfilled') {
    sequences.value = sequenceResult.value;
  } else {
    listError.value = 'Could not load sequences.';
  }

  loadingList.value = false;
  fetchYourClients();
};

// A broker's count is their own book inside each sequence's segments — the
// recipients endpoint already answers for the person asking.
const yourClients = ref({});

const clientsIn = async (sequence) => {
  try {
    const data = await estimateRecipients(sequence.segment_ids);
    return [sequence.id, data?.recipient_count ?? null];
  } catch {
    return [sequence.id, null];
  }
};

const fetchYourClients = async () => {
  if (canRunSequences.value) return;
  const withSegments = sequences.value.filter((sequence) => (sequence.segment_ids || []).length);
  yourClients.value = Object.fromEntries(await Promise.all(withSegments.map(clientsIn)));
};

const openNew = () => {
  refreshPickerOptions();
  clear();
  editingId.value = null;
  form.name = '';
  form.status = 'draft';
  form.segment_ids = [];
  form.type = 'renewal';
  form.anchor_date = '';
  form.stepsByType = freshSteps();
  saveError.value = '';
  formOpen.value = true;
};

const openEdit = (sequence) => {
  refreshPickerOptions();
  clear();
  editingId.value = sequence.id;
  form.name = sequence.name;
  form.status = sequence.status;
  form.segment_ids = [...(sequence.segment_ids || [])];
  form.type = sequence.type;
  form.stepsByType = freshSteps();

  if (sequence.type === 'scratch') {
    const chain = toChain(sequence.anchor_date || '', sequence.steps || []);
    form.anchor_date = chain.anchor;
    form.stepsByType.scratch = chain.steps;
  } else {
    form.anchor_date = '';
    form.stepsByType.renewal = (sequence.steps || []).map((step) => ({
      id: step.id,
      offset_days: step.offset_days,
      direction: step.direction,
      channels: [...step.channels],
      template_id: step.template_id,
    }));
  }

  saveError.value = '';
  formOpen.value = true;
};

const closeForm = () => {
  formOpen.value = false;
};

const firstTemplateId = () => templates.value[0]?.id ?? null;

// A step whose standard is gone starts on the first template.
const defaultRenewalStep = ({ days, key }) => {
  const id = templates.value.find((t) => t.standard_key === key)?.id;

  return {
    offset_days: days,
    direction: 'before',
    channels: CHANNELS,
    template_id: id ?? firstTemplateId(),
  };
};

const freshSteps = () => ({
  renewal: DEFAULT_RENEWAL_STEPS.map(defaultRenewalStep),
  scratch: [{ wait: 0, channels: ['email'], template_id: firstTemplateId() }],
});

const addStep = () => {
  const last = steps.value[steps.value.length - 1];
  const template_id = last?.template_id ?? firstTemplateId();

  steps.value.push(isRenewal.value
    ? { offset_days: Math.max(0, (last?.offset_days ?? 60) - 30), direction: 'before', channels: ['email'], template_id }
    : { wait: 7, channels: [...(last?.channels ?? ['email'])], template_id });
};

const updateStep = (index, updatedStep) => {
  steps.value.splice(index, 1, updatedStep);
};

const setWait = (index, value) => updateStep(index, { ...steps.value[index], wait: clampDays(value) });

// Removing the first step of a chain promotes the second to the anchor at the
// date it had already resolved to, so nothing after it moves.
const removeStep = (index) => {
  if (!isRenewal.value && index === 0 && steps.value.length > 1) {
    form.anchor_date = addDays(anchorDay.value, steps.value[1].wait);
  }
  steps.value.splice(index, 1);
};

const serverMessage = (e) => {
  const data = e?.response?.status === 422 ? e.response.data : null;
  return Object.values(data?.errors ?? {}).flat()[0] || data?.message || SAVE_FAILED;
};

// Neither store() nor update() on SequenceController accepts a status field —
// draft on create, unchanged on update — so getting to the dropdown's chosen
// status is a second call to the dedicated activate/deactivate endpoint.
const save = async () => {
  if (!canSave.value) return;
  saving.value = true;
  saveError.value = '';
  try {
    const payload = {
      name: form.name,
      type: form.type,
      anchor_date: isRenewal.value ? null : anchorDay.value,
      segment_ids: form.segment_ids,
      steps: (isRenewal.value ? steps.value : fromChain(steps.value)).map((step, i) => ({ ...step, channels: CHANNELS, position: i + 1 })),
    };

    const saved = editingId.value
      ? await updateSequence(editingId.value, payload)
      : await createSequence(payload);

    // A failed activation leaves the draft saved; a retry must update it, not create a second one.
    editingId.value = saved.id;

    if (form.status !== saved.status) {
      if (form.status === 'active') {
        await activateSequence(saved.id);
      } else {
        await deactivateSequence(saved.id);
      }
    }

    formOpen.value = false;
    window.loadNotification?.({ message: 'Sequence saved' });
  } catch (e) {
    saveError.value = serverMessage(e);
  } finally {
    saving.value = false;
    // Whatever the server actually holds — including a sequence that was
    // created but then failed to activate — is what the list must show
    // next, not just what the happy path expected.
    await fetchList();
  }
};

const deleteSelected = async () => {
  for (const id of [...selectedIds.value]) {
    try {
      await deleteSequence(id);
      sequences.value = sequences.value.filter((sequence) => sequence.id !== id);
    } catch {
      listError.value = 'Could not delete this sequence.';
    }
  }
  clear();
};

watch(
  selectedCount,
  (count) => {
    store.setToolbar({ deleteDisabled: count === 0, onDelete: deleteSelected });
  },
  { immediate: true }
);

onMounted(fetchList);
// The tabs above belong to the parent, so it is told to stand down while
// this editor owns the screen.
watch(formOpen, (open) => store.setEditing(open));

onBeforeUnmount(() => {
  store.clearToolbar();
  store.setEditing(false);
});
</script>

<template>
  <div class="sequences-tab">
    <p v-if="segmentsError" role="alert" data-testid="sequence-segments-error">
      Could not load segments. Some sequence options may be unavailable.
    </p>
    <p v-if="templatesError" role="alert" data-testid="sequence-templates-error">
      Could not load templates. Some sequence options may be unavailable.
    </p>

    <div v-if="!formOpen">
      <CommsInfoBanner :text="COMMS_HINTS.sequences">
        <template v-if="canRunSequences">
          <slot name="actions" />
          <XlButton data-testid="new-sequence" @click="openNew">
            <template #icon>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
            </template>
            New sequence
          </XlButton>
        </template>
      </CommsInfoBanner>

      <XlCard v-if="!canRunSequences" padded class="sequences-tab__note" data-testid="sequences-agency-note">
        <span class="sequences-tab__note-ico" aria-hidden="true"><svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5 11V9a2.5 2.5 0 0 1 2.5-2.5h11" /><path d="m15.6 3.6 3 3-3 3" /><path d="M19 13v2a2.5 2.5 0 0 1-2.5 2.5h-11" /><path d="m8.4 20.4-3-3 3-3" /></svg></span>
        <p>
          These run on their own, in your name, for your policyholders. The agency sets them up —
          you can see exactly what your clients receive and when.
        </p>
      </XlCard>

      <p v-if="listError" role="alert">{{ listError }}</p>
      <Loading v-else-if="loadingList" variant="rows" :rows="3" data-testid="sequences-loading" />
      <p v-else-if="!sequences.length && needsRenewalDates" class="sequences-tab__empty" data-testid="sequences-needs-dates">
        {{ SEQUENCES_NEEDS_RENEWAL_DATES }}
      </p>
      <p v-else-if="!sequences.length" class="sequences-tab__empty">
        No sequences yet. Create one to start automating renewal reminders.
      </p>

      <template v-else>
      <XlCard
        v-for="sequence in sequences"
        :key="sequence.id"
        padded
        class="sequences-tab__card"
        :class="{ 'sequences-tab__card--selected': isSelected(sequence.id) }"
        data-testid="sequence-row"
      >
        <div class="sequences-tab__head">
          <XlField
            v-if="canRunSequences"
            type="checkbox"
            :name="`sequence-${sequence.id}`"
            :aria-label="`Select ${sequence.name}`"
            data-testid="sequence-row-checkbox"
            :model-value="isSelected(sequence.id)"
            @update:model-value="(checked) => toggle(sequence.id, checked)"
          />

          <XlStatusIcon :tone="sequence.status === 'active' ? 'success' : ''"><svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 11V9a2.5 2.5 0 0 1 2.5-2.5h11" /><path d="m15.6 3.6 3 3-3 3" /><path d="M19 13v2a2.5 2.5 0 0 1-2.5 2.5h-11" /><path d="m8.4 20.4-3-3 3-3" /></svg></XlStatusIcon>

          <div class="sequences-tab__title">
            <b>{{ sequence.name }}</b>
            <span>{{ metaLine(sequence) }}</span>
          </div>

          <XlTag
            :tone="sequence.status === 'active' ? 'success' : ''"
            data-testid="sequence-status"
          >
            <XlTooltip :text="TERM_TIPS[statusLabel(sequence)]" placement="bottom">{{ statusLabel(sequence) }}</XlTooltip>
          </XlTag>

          <XlButton
            v-if="canRunSequences"
            data-testid="sequence-row-edit"
            variant="ghost"
            size="sm"
            @click="openEdit(sequence)"
          >Edit</XlButton>
          <XlTag
            v-else-if="yourClients[sequence.id] != null"
            tone="brand"
            data-testid="sequence-your-clients"
          >{{ yourClients[sequence.id] }} of your clients</XlTag>
        </div>

        <div v-if="(sequence.steps || []).length" class="sequences-tab__steps">
          <template v-for="(step, index) in sequence.steps" :key="step.id">
            <span class="sequences-tab__chip" data-testid="sequence-step-chip">{{ stepChipLabel(sequence, step) }}</span>

            <span
              v-if="gapAfter(sequence, index)"
              class="sequences-tab__gap"
              data-testid="sequence-step-gap"
            >{{ gapAfter(sequence, index) }}</span>
            <span v-else-if="index < sequence.steps.length - 1" class="sequences-tab__chev"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></span>
          </template>
        </div>
      </XlCard>
      </template>
    </div>

    <CommsEditorPage
      v-else
      testid="sequence-editor"
      :changes="form"
      back-label="Back to sequences"
      :title="editingId ? 'Edit sequence' : 'New sequence'"
      save-label="Save sequence"
      :save-disabled="!canSave || saving"
      @back="closeForm"
      @cancel="closeForm"
      @save="save"
    >
      <div class="sequence-editor">
        <XlField
          v-model="form.name"
          name="sequence_name"
          data-testid="sequence-name-input"
          label="Sequence name"
          :tip="TERM_TIPS.Sequence"
          :maxlength="NAME_MAX"
          :counted="false"
          placeholder="e.g. Home renewal reminders"
        />

        <CommsChoiceGroup
          data-testid="sequence-status-select"
          testid="sequence-status"
          label="Status"
          equal
          :options="STATUS_OPTIONS"
          v-model="form.status"
        />

        <SegmentMultiselect
          data-testid="sequence-segments"
          :model-value="form.segment_ids"
          :segments="segments"
          @update:model-value="(ids) => (form.segment_ids = ids)"
        />

        <CommsChoiceGroup
          data-testid="sequence-anchor"
          testid="sequence-type"
          label="Type"
          equal
          :options="ANCHOR_OPTIONS"
          v-model="form.type"
        />

        <div class="sequence-editor__note" data-testid="sequence-type-note">
          <svg v-if="isRenewal" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 11V9a2.5 2.5 0 0 1 2.5-2.5h11" /><path d="m15.6 3.6 3 3-3 3" /><path d="M19 13v2a2.5 2.5 0 0 1-2.5 2.5h-11" /><path d="m8.4 20.4-3-3 3-3" /></svg>
          <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
          <p v-if="isRenewal">
            Every step counts from each policyholder's <b>own renewal date</b>, so one step reaches
            different people on different days. There is no calendar date to show.
          </p>
          <p v-else>
            <b>Step 1 is a date everyone gets.</b> Each step after it waits a number of days from the
            step before — a chain. For something on its own calendar: a storm warning, a welcome series.
          </p>
        </div>

        <div class="sequence-editor__field">
          <span class="sequence-editor__label">Steps</span>
          <div v-if="steps.length" class="sequence-editor__steps">
            <template v-for="(step, index) in steps" :key="`${form.type}-${index}`">
              <div v-if="!isRenewal && index > 0" class="sequence-editor__rail" data-testid="step-wait">
                <span>wait</span>
                <XlField
                  type="number"
                  :name="`step-wait-${index}`"
                  aria-label="Days to wait"
                  min="0"
                  max="365"
                  :model-value="step.wait"
                  @change="(event) => setWait(index, event.target.value)"
                />
                <span>days, then</span>
              </div>
              <SequenceStepRow
                :model-value="step"
                :number="index + 1"
                :heading="stepHeading(step, index)"
                :templates="templates"
                :renewal="isRenewal"
                @update:model-value="(updated) => updateStep(index, updated)"
                @remove="removeStep(index)"
              >
                <template v-if="!isRenewal && index === 0" #date>
                  <XlDateTimeField
                    v-model="form.anchor_date"
                    required
                    data-testid="sequence-anchor-date"
                    :min="form.status === 'active' ? todayDateString() : ''"
                  />
                  <p v-if="activationDateError" class="sequence-editor__date-error" role="alert" data-testid="activation-date-error">
                    {{ activationDateError }}
                  </p>
                </template>
              </SequenceStepRow>
            </template>
          </div>
          <p v-else class="sequence-editor__muted" data-testid="sequence-no-steps">
            A sequence with no steps never sends. Add one.
          </p>
          <p v-if="outOfOrder" class="sequence-editor__warn" role="status" data-testid="sequence-out-of-order">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
            <span>These run furthest-out first, so they'll send in a different order than they read here.</span>
          </p>
          <div class="sequence-editor__add-step">
            <XlButton variant="ghost" size="sm" data-testid="add-step" @click="addStep">
              <template #icon>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                </svg>
              </template>
              Add step
            </XlButton>
          </div>
        </div>

        <p v-if="saveError" role="alert" data-testid="save-error">{{ saveError }}</p>
      </div>

      <template #aside>
        <SequenceRunPanel
          :rows="runRows"
          :segment-names="selectedSegmentNames"
          :recipient-count="recipientCount"
          :anchor="form.type"
          :span="runSpan"
        />
      </template>
    </CommsEditorPage>
  </div>
</template>

<style scoped lang="scss">
.sequences-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__empty {
    color: #767676;
    font-size: 14px;
  }

    &__new { display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-bottom: 16px; }

  &__note {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    font-size: 13.5px;
    color: var(--xl-color-secondary-text);

    p { margin: 0; }
  }

  &__note-ico {
    display: grid;
    flex: 0 0 42px;
    place-items: center;
    width: 42px;
    height: 42px;
    color: var(--xl-color-primary-text);
  }

  &__card--selected { border-color: var(--xl-color-primary); }

  &__head {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  &__title {
    flex: 1;
    min-width: 200px;

    b {
      display: block;
      font-size: 17px;
      line-height: 23px;
      color: var(--xl-color-primary-text);
    }

    span {
      display: block;
      font-size: 13.5px;
      line-height: 18px;
      color: var(--xl-color-outline);
    }
  }

  &__steps {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 13px;
    border-radius: 7px;
    background: var(--xl-color-primary-container);
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    color: var(--xl-color-primary);
  }

  &__gap, &__chev {
    font-size: 12px;
    color: var(--xl-color-outline-variant);
  }

  &__chev { font-size: 16px; line-height: 1; }
}

.sequence-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: inherit;

  &__note {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    margin-top: -7px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--xl-color-border-subtle, #f6f2f8);
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--xl-color-secondary-text);

    svg { flex: 0 0 auto; margin-top: 2px; color: var(--xl-color-primary); }
    p { margin: 0; }
  }

  &__field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__label {
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  // No overflow clip: each card carries dropdowns whose menu is absolutely
  // positioned and taller than the card, so anything hiding overflow here cuts
  // the open menu off at the container edge.
  // No gap where a wait rail joins two cards, which it must touch to read as
  // a line; cards that sit straight after each other get room to breathe.
  &__steps {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 0;
    margin-bottom: 12px;

    > .step-card + .step-card { margin-top: 12px; }
  }

  // The wait sits between the two steps it separates, on the line that joins them.
  &__rail {
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 0 0 0 11px;
    padding: 9px 0 9px 20px;
    border-left: 2px dashed var(--xl-color-border, #ede7f0);
    font-size: 12.5px;
    color: var(--xl-color-outline);

    :deep(.xl-field) { width: 62px; }
    :deep(input.xl-field__control:not(.xl-field__control--tick)) { height: 34px; padding: 0 10px; text-align: center; color: inherit; }
  }

  &__date-error {
    margin: 6px 0 0;
    font-size: 12px;
    color: var(--xl-color-error);
  }

  &__muted {
    margin: 0 0 10px;
    font-size: 13px;
    color: var(--xl-color-outline);
  }

  &__warn {
    display: flex;
    gap: 7px;
    align-items: flex-start;
    margin: 0 0 10px;
    font-size: 12.5px;
    color: var(--xl-color-warning);

    svg { flex: 0 0 auto; margin-top: 2px; }
  }

  &__add-step {
    display: flex;
  }
}
</style>
